import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Heart, Star, Truck, ShieldCheck, Ruler } from "lucide-react";
import { toast } from "sonner";

export default function ProductPage() {
  const { id } = useParams();
  const { addItem, recordView } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("M");
  const [view, setView] = useState("front");
  const [reviews, setReviews] = useState({ reviews: [], avg: 0, count: 0 });
  const [newReview, setNewReview] = useState({ rating: 5, text: "" });

  useEffect(() => {
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data.product);
      setRelated(r.data.related);
      setColor(r.data.product.colors?.[0]);
      recordView(r.data.product);
    });
    api.get(`/reviews/${id}`).then(r => setReviews(r.data));
  }, [id, recordView]);

  // SEO: inject title + meta description + keywords when product loads
  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} \u2014 Hampton Blu Creations`;
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", (product.description || "").slice(0, 160));
    if (product.tags?.length) setMeta("keywords", product.tags.join(", "));
    setMeta("og:title", `${product.name} \u2014 Hampton Blu Creations`, true);
    setMeta("og:description", (product.description || "").slice(0, 200), true);
    const firstMock = Object.values(product.mockups || {})[0];
    const mockUrl = firstMock?.front || firstMock?.back;
    if (mockUrl) setMeta("og:image", `${window.location.origin}/api/files/${mockUrl}`, true);
    setMeta("og:type", "product", true);
    setMeta("twitter:card", "summary_large_image");
    return () => { document.title = "Hampton Blu Creations"; };
  }, [product]);

  if (!product) return <div className="py-32 text-center text-sm text-neutral-500">Loading…</div>;

  const price = ["3XL","4XL","5XL"].includes(size) ? product.base_price + (product.xl_addon || 0) : product.base_price;
  const mockup = product.mockups?.[color]?.[view] || product.mockups?.[color]?.front;

  const onAdd = () => {
    addItem({ product_id: product.product_id, color, size, qty: 1, name: product.name, price, mockup });
    toast.success("Added to cart");
  };
  const onWishlist = async () => {
    if (!user) { toast("Sign in to save to wishlist"); return; }
    try {
      await api.post("/wishlist", { product_id: product.product_id });
      toast.success("Saved to wishlist");
    } catch { toast.error("Could not save"); }
  };
  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to leave a review"); return; }
    try {
      await api.post("/reviews", { product_id: id, rating: newReview.rating, text: newReview.text });
      setNewReview({ rating: 5, text: "" });
      const r = await api.get(`/reviews/${id}`); setReviews(r.data);
      toast.success("Review posted");
    } catch { toast.error("Error"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10" data-testid={`product-page-${product.product_id}`}>
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-[color:var(--hb-warm)] overflow-hidden" data-testid="product-main-image">
            {mockup && <img src={fileUrl(mockup)} alt={product.name} className="w-full h-full object-cover" />}
          </div>
          {product.has_back && (
            <div className="flex gap-3 mt-3">
              <button onClick={()=>setView("front")} className={`flex-1 py-2 text-xs uppercase tracking-widest border ${view==="front"?"border-[color:var(--hb-navy)] bg-[color:var(--hb-navy)] text-white":"border-neutral-300"}`} data-testid="view-front-btn">Front</button>
              <button onClick={()=>setView("back")} className={`flex-1 py-2 text-xs uppercase tracking-widest border ${view==="back"?"border-[color:var(--hb-navy)] bg-[color:var(--hb-navy)] text-white":"border-neutral-300"}`} data-testid="view-back-btn">Back</button>
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-2">Hampton Blu Creations</div>
          <h1 className="font-display text-5xl text-[color:var(--hb-navy)] leading-none">{product.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex text-[color:var(--hb-royal)]">
              {[1,2,3,4,5].map(n => <Star key={n} size={14} fill={n<=Math.round(reviews.avg)?"currentColor":"none"} />)}
            </div>
            <span className="text-xs text-neutral-500">{reviews.count} reviews</span>
          </div>
          <div className="mt-6 text-3xl font-medium text-[color:var(--hb-navy)]" data-testid="product-price">${price.toFixed(2)}</div>
          <p className="mt-4 text-neutral-700 leading-relaxed">{product.description}</p>

          {/* Color */}
          <div className="mt-8">
            <div className="text-xs tracking-widest uppercase text-neutral-500 mb-3">Color: <span className="text-neutral-900">{color}</span></div>
            <div className="flex flex-wrap gap-3">
              {product.colors?.map(c => (
                <button key={c} onClick={()=>setColor(c)} title={c}
                  className={`w-9 h-9 rounded-full border-2 ${color===c?"border-[color:var(--hb-navy)]":"border-neutral-200"}`}
                  style={{background: product.color_hex?.[c]}}
                  data-testid={`color-${c.toLowerCase().replace(/ /g,'-')}`}
                />
              ))}
            </div>
          </div>

          {/* Size */}
          <div className="mt-6">
            <div className="text-xs tracking-widest uppercase text-neutral-500 mb-3 flex items-center gap-2"><Ruler size={12}/> Size: <span className="text-neutral-900">{size}</span></div>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map(s => (
                <button key={s} onClick={()=>setSize(s)}
                  className={`min-w-[52px] px-3 py-2 text-sm border ${size===s?"border-[color:var(--hb-navy)] bg-[color:var(--hb-navy)] text-white":"border-neutral-300 hover:border-neutral-500"}`}
                  data-testid={`size-${s}`}>
                  {s}
                </button>
              ))}
            </div>
            {["3XL","4XL","5XL"].includes(size) && <div className="text-xs mt-2 text-[color:var(--hb-royal)]">+${(product.xl_addon||0).toFixed(2)} for extended sizes</div>}
          </div>

          <div className="mt-8 flex gap-3">
            <button onClick={onAdd} className="hb-btn-primary flex-1" data-testid="add-to-cart-btn">Add to Cart</button>
            <button onClick={onWishlist} className="hb-btn-secondary" aria-label="Wishlist" data-testid="wishlist-btn"><Heart size={16}/></button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-neutral-200">
            <div className="flex items-center gap-2 text-xs text-neutral-600"><Truck size={14}/> Free ship $75+</div>
            <div className="flex items-center gap-2 text-xs text-neutral-600"><ShieldCheck size={14}/> Secure checkout</div>
            <div className="flex items-center gap-2 text-xs text-neutral-600"><Star size={14}/> Family made</div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-20 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl text-[color:var(--hb-navy)] mb-4">Reviews ({reviews.count})</h2>
          <div className="space-y-6" data-testid="reviews-list">
            {reviews.reviews.length === 0 && <p className="text-sm text-neutral-500">Be the first to review.</p>}
            {reviews.reviews.map(r => (
              <div key={r.review_id} className="border-b border-neutral-200 pb-4">
                <div className="flex items-center gap-2">
                  <div className="flex text-[color:var(--hb-royal)]">
                    {[1,2,3,4,5].map(n => <Star key={n} size={12} fill={n<=r.rating?"currentColor":"none"} />)}
                  </div>
                  <span className="text-xs font-medium">{r.user_name}</span>
                </div>
                <p className="text-sm mt-2 text-neutral-700">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display text-2xl text-[color:var(--hb-navy)] mb-4">Leave a Review</h3>
          <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
            <select value={newReview.rating} onChange={e=>setNewReview({...newReview, rating: Number(e.target.value)})} className="border border-neutral-300 px-3 py-2 text-sm" data-testid="review-rating">
              {[5,4,3,2,1].map(n=><option key={n} value={n}>{n} stars</option>)}
            </select>
            <textarea value={newReview.text} onChange={e=>setNewReview({...newReview, text: e.target.value})} required
              placeholder="Share your thoughts…" rows="4" className="w-full border border-neutral-300 px-3 py-2 text-sm" data-testid="review-text"/>
            <button className="hb-btn-primary" data-testid="review-submit">Post Review</button>
          </form>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-3xl text-[color:var(--hb-navy)] mb-6">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {related.slice(0,4).map(p => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
